import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import ResumeAnalyzer from './ResumeAnalyzer';
import AdminPanel from './AdminPanel';
import authService from './services/authService';
import './App.css';
import VerifyEmail from './components/Auth/VerifyEmail';
import UserProfile from './components/UserProfile/UserProfile';
import CheckEmailPage from './components/Auth/CheckEmailPage';

//Google Client ID from environment variable
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Debug logging to verify Client ID is loaded correctly
console.log('[APP.JS DEBUG] Environment variables check:');
console.log('[APP.JS DEBUG] REACT_APP_GOOGLE_CLIENT_ID:', GOOGLE_CLIENT_ID);
console.log('[APP.JS DEBUG] Client ID length:', GOOGLE_CLIENT_ID ? GOOGLE_CLIENT_ID.length : 'undefined');
console.log('[APP.JS DEBUG] Client ID starts with:', GOOGLE_CLIENT_ID ? GOOGLE_CLIENT_ID.substring(0, 20) + '...' : 'undefined');

// Validate Client ID
if (!GOOGLE_CLIENT_ID) {
  console.error('[APP.JS ERROR] Google Client ID is not defined!');
  console.error('[APP.JS ERROR] Make sure .env file has REACT_APP_GOOGLE_CLIENT_ID set');
} else if (!GOOGLE_CLIENT_ID.includes('.apps.googleusercontent.com')) {
  console.error('[APP.JS ERROR] Google Client ID format looks incorrect!');
  console.error('[APP.JS ERROR] It should end with .apps.googleusercontent.com');
}

// Protected Route Component
const ProtectedRoute = ({ children, isAuthenticated, isLoading }) => {
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}>
        <div style={{ color: 'white', fontSize: '18px' }}>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return children;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is already logged in on app load
  useEffect(() => {
    const checkAuth = () => {
      const token = authService.getToken();
      const user = authService.getStoredUser();

      console.log('[APP.JS DEBUG] Checking authentication...');
      console.log('[APP.JS DEBUG] Token exists:', !!token);
      console.log('[APP.JS DEBUG] User exists:', !!user);

      if (token && user) {
        console.log('[APP.JS DEBUG] User is authenticated');
        setIsAuthenticated(true);
      } else {
        console.log('[APP.JS DEBUG] User is not authenticated');
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  // Handle successful login
  const handleLoginSuccess = () => {
    console.log('[APP.JS DEBUG] handleLoginSuccess called - setting isAuthenticated to true');
    setIsAuthenticated(true);
  };

  // If Google Client ID is missing, show error page
  if (!GOOGLE_CLIENT_ID) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px',
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '10px',
          maxWidth: '500px',
          textAlign: 'center',
        }}>
          <h2 style={{ color: '#e74c3c', marginBottom: '20px' }}>Configuration Error</h2>
          <p style={{ color: '#555', marginBottom: '20px' }}>
            Google OAuth Client ID is not configured. Please contact your administrator.
          </p>
          <p style={{ color: '#999', fontSize: '14px' }}>
            Error: REACT_APP_GOOGLE_CLIENT_ID environment variable is missing
          </p>
        </div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-email-check" element={<CheckEmailPage />} />

          {/* Protected Routes - AdminPanel now handles its own navigation */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
                <AdminPanel />
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
                <UserProfile />
              </ProtectedRoute>
            }
          />

          <Route
            path="/resume-analyzer"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
                <ResumeAnalyzer />
              </ProtectedRoute>
            }
          />

          {/* Default redirect */}
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" />
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </GoogleOAuthProvider>
  );
}

export default App;